import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Send, CheckCircle2, Loader2, ArrowLeft, Mail, Building2, MessageSquare } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import TeroumaLogo from "../components/ui/TeroumaLogo";
import { PUBLIC_API } from "../lib/api";

export default function ContactPage() {
  const [params] = useSearchParams();
  const isOrg = params.get("type") === "org";

  const [type, setType] = useState(isOrg ? "org" : "general");
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.message) {
      toast.error("Veuillez remplir tous les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${PUBLIC_API}/contact`, {
        ...form,
        contact_type: type,
        subject: form.subject || (type === "org" ? "Intérêt pour rejoindre Terouma" : "Message via Terouma"),
      });
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible — réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#F4F3EF", fontFamily: "'Outfit', sans-serif" }}>
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/"><TeroumaLogo textColor="#0D1B33" /></Link>
          <Link to="/login" className="text-sm text-stone-500 hover:text-stone-900 transition-colors">Connexion</Link>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-6 py-14">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-900 mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Retour à l'accueil
        </Link>

        {done ? (
          <div className="bg-white rounded-3xl p-12 text-center shadow-sm border border-stone-100">
            <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            </div>
            <h2 className="text-2xl font-semibold text-stone-900 mb-2">Message envoyé !</h2>
            <p className="text-stone-500 text-sm mb-2">Nous vous répondrons dans les plus brefs délais.</p>
            <p className="text-xs text-stone-400 mb-8">Un email de confirmation a été envoyé à {form.email}</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white text-sm font-medium" style={{ background: "#0D1B33" }}>
              Retour à l'accueil
            </Link>
          </div>
        ) : (
          <div className="bg-white rounded-3xl p-8 shadow-sm border border-stone-100">
            <h1 className="text-3xl font-light text-stone-900 mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Nous contacter
            </h1>
            <p className="text-stone-500 text-sm mb-8">Une question, un partenariat ou une demande ? Écrivez-nous.</p>

            {/* Type selector */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {[
                { v: "general", l: "Question / Message", icon: MessageSquare, desc: "Demande générale" },
                { v: "org",     l: "Mon organisation",   icon: Building2,     desc: "Rejoindre Terouma" },
              ].map(t => {
                const Icon = t.icon;
                return (
                  <button key={t.v} type="button" onClick={() => setType(t.v)}
                    className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                      type === t.v ? "border-stone-900 bg-stone-50" : "border-stone-200 hover:border-stone-300"
                    }`}>
                    <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${type === t.v ? "text-stone-900" : "text-stone-400"}`} />
                    <div>
                      <p className={`text-sm font-medium ${type === t.v ? "text-stone-900" : "text-stone-600"}`}>{t.l}</p>
                      <p className="text-xs text-stone-400">{t.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            {type === "org" && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6 text-sm text-amber-800">
                Décrivez votre organisation (nom, ville, nombre de membres) et nous vous contacterons pour configurer votre espace.
              </div>
            )}

            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-1.5">Prénom *</label>
                  <input value={form.first_name} onChange={e => set("first_name", e.target.value)} required
                    className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10" />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-1.5">Nom *</label>
                  <input value={form.last_name} onChange={e => set("last_name", e.target.value)} required
                    className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10" />
                </div>
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-1.5">Email *</label>
                <input type="email" value={form.email} onChange={e => set("email", e.target.value)} required
                  className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10" />
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-1.5">Téléphone</label>
                <input type="tel" value={form.phone} onChange={e => set("phone", e.target.value)}
                  placeholder="+33 6 12 34 56 78"
                  className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10" />
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-1.5">Objet</label>
                <input value={form.subject} onChange={e => set("subject", e.target.value)}
                  placeholder={type === "org" ? "Rejoindre Terouma — [Nom de l'organisation]" : "Votre sujet"}
                  className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10" />
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-[0.15em] text-stone-500 mb-1.5">Message *</label>
                <textarea rows={5} value={form.message} onChange={e => set("message", e.target.value)} required
                  placeholder={type === "org"
                    ? "Présentez votre organisation : nom, ville, taille de la communauté, et ce que vous recherchez…"
                    : "Votre message…"}
                  className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-stone-900/10 resize-none" />
              </div>

              <button type="submit" disabled={loading}
                className="w-full py-3 rounded-xl text-white text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
                style={{ background: "#0D1B33" }}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Envoyer le message
              </button>

              <p className="text-[11px] text-stone-400 text-center">
                Réponse sous 24-48h ouvrées
              </p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
